import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { PageHeader } from "@/features/power_projects/components/ui";
import {
  useCreateTakeoffRule,
  useDeleteTakeoffRule,
  useTakeoffRules,
  useTakeoffVariables,
  useUpdateTakeoffRule,
} from "../hooks";
import type {
  TakeoffCondition,
  TakeoffConditionOp,
  TakeoffRule,
  TakeoffRuleFormData,
  TakeoffRuleKind,
  TakeoffVariables,
} from "../types";

const CATEGORIES: [string, string][] = [
  ["budowlana", "Budowlana"],
  ["ppn", "PPN"],
  ["biuro", "Biuro"],
  ["inne", "Inne"],
];

function errorText(err: unknown): string | null {
  const e = err as { response?: { data?: { errors?: unknown } } };
  const errs = e?.response?.data?.errors;
  if (!errs) return null;
  if (typeof errs === "string") return errs;
  if (Array.isArray(errs)) return errs.join(" ");
  return Object.entries(errs as Record<string, unknown>)
    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(" ") : String(v)}`)
    .join(" · ");
}

/** Warunki reguły — dopasowują cokolwiek (tekst, liczbę, tak/nie), w odróżnieniu od
 *  wzoru, który liczy wyłącznie na liczbach. Ten podział jest celowy: dzięki niemu na
 *  regule od razu widać, czego dotyczy, zanim policzy się ile tego jest. */
function ConditionsEditor({
  value,
  paramKeys,
  ops,
  onChange,
}: {
  value: TakeoffCondition[];
  paramKeys: string[];
  ops: Record<string, string>;
  onChange: (next: TakeoffCondition[]) => void;
}) {
  const set = (i: number, patch: Partial<TakeoffCondition>) =>
    onChange(value.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  return (
    <div className="space-y-1.5">
      {value.length === 0 && (
        <p className="text-[11px] text-[var(--color-text-tertiary)]">
          Bez warunków — reguła dotyczy każdego elementu tego typu.
        </p>
      )}
      {value.map((cond, i) => (
        <div key={i} className="flex flex-wrap items-center gap-1.5">
          <select
            className="input py-1 text-[12px]"
            value={cond.key}
            onChange={(e) => set(i, { key: e.target.value })}
          >
            <option value="">— parametr —</option>
            {paramKeys.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
          <select
            className="input py-1 text-[12px]"
            value={cond.op}
            onChange={(e) => set(i, { op: e.target.value as TakeoffConditionOp })}
          >
            {Object.entries(ops).map(([op, label]) => (
              <option key={op} value={op}>
                {label}
              </option>
            ))}
          </select>
          <input
            className="input flex-1 py-1 font-mono text-[12px]"
            value={String(cond.value ?? "")}
            placeholder="wartość"
            onChange={(e) => set(i, { value: e.target.value })}
          />
          <button
            className="text-[11px] text-[var(--color-text-tertiary)] hover:text-red-500"
            onClick={() => onChange(value.filter((_, idx) => idx !== i))}
          >
            Usuń
          </button>
        </div>
      ))}
      <button
        className="text-[11px] text-[var(--color-primary)] hover:underline"
        onClick={() => onChange([...value, { key: "", op: "eq", value: "" }])}
      >
        + warunek
      </button>
    </div>
  );
}

function RuleForm({
  vars,
  initial,
  onCancel,
  onSubmit,
  saving,
  error,
}: {
  vars: TakeoffVariables;
  initial?: TakeoffRule;
  onCancel: () => void;
  onSubmit: (data: TakeoffRuleFormData) => void;
  saving: boolean;
  error: string | null;
}) {
  const [kind, setKind] = useState<TakeoffRuleKind>(initial?.kind ?? "position");
  const [elementType, setElementType] = useState(
    initial?.element_type ?? vars.element_types[0]?.element_type ?? "",
  );
  const [conditions, setConditions] = useState<TakeoffCondition[]>(
    initial?.conditions ?? [],
  );
  const [section, setSection] = useState(initial?.section ?? "");
  const [basis, setBasis] = useState(initial?.basis ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [unit, setUnit] = useState(initial?.unit ?? "");
  const [expr, setExpr] = useState(initial?.quantity_expr ?? "");
  const [category, setCategory] = useState(initial?.category ?? "budowlana");
  const [requires, setRequires] = useState(initial?.requires_element_type ?? "");
  const [warning, setWarning] = useState(initial?.warning ?? "");
  const [note, setNote] = useState(initial?.note ?? "");

  const et = vars.element_types.find((e) => e.element_type === elementType);
  const paramKeys = (et?.param_schema ?? []).map((p) => p.key);

  const submit = () =>
    onSubmit({
      kind,
      element_type: elementType,
      conditions,
      section,
      basis,
      description,
      unit,
      quantity_expr: expr,
      category,
      requires_element_type: kind === "check" ? requires || null : null,
      warning,
      note,
    });

  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-[var(--color-text-tertiary)]">Rodzaj</span>
          <select
            className="input py-1 text-[13px]"
            value={kind}
            onChange={(e) => setKind(e.target.value as TakeoffRuleKind)}
          >
            <option value="position">Tworzy pozycję przedmiaru</option>
            <option value="check">Sprawdza kompletność (ostrzeżenie)</option>
          </select>
        </label>
        <label className="flex flex-1 flex-col gap-1">
          <span className="text-[11px] text-[var(--color-text-tertiary)]">
            Element rysunku
          </span>
          <select
            className="input py-1 text-[13px]"
            value={elementType}
            onChange={(e) => {
              setElementType(e.target.value);
              // Warunki odnoszą się do parametrów poprzedniego typu — po zmianie
              // elementu przestają cokolwiek znaczyć, więc lepiej je wyczyścić niż
              // zostawić ciche „nigdy nie pasuje".
              setConditions([]);
            }}
          >
            {vars.element_types.map((e) => (
              <option key={e.element_type} value={e.element_type}>
                {e.label} ({e.kind === "line" ? "linia" : e.kind === "area" ? "obszar" : "obiekt"})
              </option>
            ))}
          </select>
        </label>
      </div>

      <div>
        <p className="section-label mb-1">Kiedy reguła działa</p>
        <ConditionsEditor
          value={conditions}
          paramKeys={paramKeys}
          ops={vars.condition_ops}
          onChange={setConditions}
        />
      </div>

      {kind === "check" ? (
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-[var(--color-text-tertiary)]">
              Wymaga obecności elementu
            </span>
            <select
              className="input py-1 text-[13px]"
              value={requires ?? ""}
              onChange={(e) => setRequires(e.target.value)}
            >
              <option value="">— wybierz —</option>
              {vars.element_types.map((e) => (
                <option key={e.element_type} value={e.element_type}>
                  {e.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-1 flex-col gap-1">
            <span className="text-[11px] text-[var(--color-text-tertiary)]">
              Treść ostrzeżenia
            </span>
            <input
              className="input py-1 text-[13px]"
              value={warning}
              placeholder="np. Jest przewiert, a nie ma rury osłonowej — sprawdź."
              onChange={(e) => setWarning(e.target.value)}
            />
          </label>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-[var(--color-text-tertiary)]">Dział</span>
              <input
                className="input w-44 py-1 text-[13px]"
                value={section}
                placeholder="Roboty ziemne"
                onChange={(e) => setSection(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-[var(--color-text-tertiary)]">
                Podstawa KNR/KNNR
              </span>
              <input
                className="input w-44 py-1 font-mono text-[13px]"
                value={basis}
                placeholder="KNNR 5 0701-04"
                onChange={(e) => setBasis(e.target.value)}
              />
            </label>
            <label className="flex flex-1 flex-col gap-1">
              <span className="text-[11px] text-[var(--color-text-tertiary)]">
                Opis pozycji
              </span>
              <input
                className="input py-1 text-[13px]"
                value={description}
                placeholder="Wykop pod kabel, grunt kat. {kat_gruntu}"
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-[var(--color-text-tertiary)]">
                Jednostka
              </span>
              <input
                className="input w-24 py-1 text-[13px]"
                value={unit}
                placeholder="m3"
                onChange={(e) => setUnit(e.target.value)}
              />
            </label>
            <label className="flex flex-1 flex-col gap-1">
              <span className="text-[11px] text-[var(--color-text-tertiary)]">
                Wzór na ilość
              </span>
              <input
                className="input py-1 font-mono text-[13px]"
                value={expr}
                placeholder="dlugosc_m * szerokosc_m * glebokosc_m"
                onChange={(e) => setExpr(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-[var(--color-text-tertiary)]">
                Kategoria
              </span>
              <select
                className="input py-1 text-[13px]"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {CATEGORIES.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="font-mono text-[10px] text-[var(--color-text-muted)]">
            Zmienne: {(et?.variables ?? []).join(", ") || "—"}
          </p>
          <p className="text-[10px] text-[var(--color-text-tertiary)]">
            Podstawę można zostawić pustą — wtedy pozycja idzie do wyceny z samym opisem
            i ilością, a dopasowaniem zajmuje się AI. Parametry tekstowe (np. kategoria
            gruntu) rozstrzyga się warunkiem, nie wzorem.
          </p>
        </>
      )}

      <label className="flex flex-col gap-1">
        <span className="text-[11px] text-[var(--color-text-tertiary)]">
          Uwagi (opcjonalnie)
        </span>
        <input
          className="input py-1 text-[13px]"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>

      {error && (
        <p className="font-mono text-[12px] text-[var(--color-primary)]">{error}</p>
      )}

      <div className="flex items-center gap-2">
        <Button size="sm" loading={saving} onClick={submit}>
          {initial ? "Zapisz zmiany" : "Dodaj regułę"}
        </Button>
        <button
          className="text-[12px] text-[var(--color-text-tertiary)] hover:underline"
          onClick={onCancel}
        >
          Anuluj
        </button>
      </div>
    </div>
  );
}

function RuleRow({
  rule,
  onEdit,
  onToggle,
  onDelete,
}: {
  rule: TakeoffRule;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const isCheck = rule.kind === "check";
  return (
    <div
      className={`flex flex-wrap items-start justify-between gap-3 border-t border-[var(--color-border-muted)] px-3 py-2 ${
        rule.is_active ? "" : "opacity-50"
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          {isCheck ? (
            <span className="rounded-sm border border-amber-200 bg-amber-50 px-1.5 py-0.5 font-mono text-[10px] uppercase text-amber-700">
              kontrola
            </span>
          ) : (
            rule.basis && (
              <span className="font-mono text-[11px] text-[var(--color-text-tertiary)]">
                {rule.basis}
              </span>
            )
          )}
          <span className="text-[13px] text-[var(--color-text-primary)]">
            {isCheck ? rule.warning : rule.description}
          </span>
          {!isCheck && !rule.basis && (
            <span className="font-mono text-[10px] text-amber-600">bez podstawy → AI</span>
          )}
        </div>
        <p className="mt-0.5 font-mono text-[11px] text-[var(--color-text-tertiary)]">
          {isCheck
            ? `wymaga: ${rule.requires_element_type_label ?? "—"}`
            : `${rule.quantity_expr} [${rule.unit}]`}
          {rule.conditions.length > 0 &&
            ` · gdy ${rule.conditions.map((c) => `${c.key} ${c.op} ${c.value}`).join(" i ")}`}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <button
          className="text-[11px] text-[var(--color-text-tertiary)] hover:text-[var(--color-primary)]"
          onClick={onToggle}
        >
          {rule.is_active ? "Wyłącz" : "Włącz"}
        </button>
        <button
          className="text-[11px] text-[var(--color-text-tertiary)] hover:text-[var(--color-primary)]"
          onClick={onEdit}
        >
          Edytuj
        </button>
        {confirming ? (
          <>
            <button
              className="text-[11px] font-semibold text-red-600 hover:underline"
              onClick={onDelete}
            >
              Usuń na pewno
            </button>
            <button
              className="text-[11px] text-[var(--color-text-tertiary)]"
              onClick={() => setConfirming(false)}
            >
              Anuluj
            </button>
          </>
        ) : (
          <button
            className="text-[11px] text-[var(--color-text-tertiary)] hover:text-red-500"
            onClick={() => setConfirming(true)}
          >
            Usuń
          </button>
        )}
      </div>
    </div>
  );
}

export function TakeoffRulesPage() {
  const { data: rules, isLoading } = useTakeoffRules();
  const { data: vars } = useTakeoffVariables();
  const create = useCreateTakeoffRule();
  const update = useUpdateTakeoffRule();
  const remove = useDeleteTakeoffRule();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<TakeoffRule | null>(null);

  const grouped = useMemo(() => {
    const map = new Map<string, TakeoffRule[]>();
    for (const r of rules ?? []) {
      const key = r.element_type_label || r.element_type_code;
      (map.get(key) ?? map.set(key, []).get(key)!).push(r);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], "pl"));
  }, [rules]);

  const covered = new Set((rules ?? []).map((r) => r.element_type));
  const uncovered = (vars?.element_types ?? []).filter(
    (e) => !covered.has(e.element_type),
  );

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 md:p-6">
      <PageHeader
        title="Reguły obmiaru"
        subtitle="Jak narysowany element zamienia się w pozycję przedmiaru"
        backTo="/estimating"
      />

      <div className="card space-y-2 p-4 text-sm text-[var(--color-text-secondary)]">
        <p>
          Reguła mówi: „dla elementu typu X, przy takich parametrach, wygeneruj pozycję
          o podstawie KNNR&nbsp;… i ilości liczonej wzorem". Elementy o identycznych
          parametrach scalą się w jedną pozycję; różniące się rozejdą — i widać dlaczego.
        </p>
        <p className="text-[var(--color-text-tertiary)]">
          Element bez żadnej pasującej reguły nigdy nie znika po cichu — trafia na listę
          kontrolną przy wygenerowanym kosztorysie.
        </p>
      </div>

      {!adding && !editing && (
        <Button size="sm" onClick={() => setAdding(true)}>
          + Nowa reguła
        </Button>
      )}

      {vars && adding && (
        <RuleForm
          vars={vars}
          saving={create.isPending}
          error={errorText(create.error)}
          onCancel={() => setAdding(false)}
          onSubmit={(data) =>
            create.mutate(data, { onSuccess: () => setAdding(false) })
          }
        />
      )}
      {vars && editing && (
        <RuleForm
          vars={vars}
          initial={editing}
          saving={update.isPending}
          error={errorText(update.error)}
          onCancel={() => setEditing(null)}
          onSubmit={(data) =>
            update.mutate(
              { id: editing.id, data },
              { onSuccess: () => setEditing(null) },
            )
          }
        />
      )}

      {isLoading && <p className="section-label">Ładowanie…</p>}

      {grouped.map(([label, items]) => (
        <div key={label} className="card">
          <p className="px-3 pt-3 section-label">{label}</p>
          {items.map((rule) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              onEdit={() => {
                setAdding(false);
                setEditing(rule);
              }}
              onToggle={() =>
                update.mutate({ id: rule.id, data: { is_active: !rule.is_active } })
              }
              onDelete={() => remove.mutate(rule.id)}
            />
          ))}
        </div>
      ))}

      {uncovered.length > 0 && (
        <div className="card p-4">
          <p className="section-label mb-1">Elementy bez żadnej reguły</p>
          <p className="mb-2 text-[11px] text-[var(--color-text-tertiary)]">
            Narysowane, ale nie wejdą do żadnej pozycji przedmiaru. To nie jest błąd —
            słownik ma rosnąć stopniowo — ale warto wiedzieć, czego jeszcze brakuje.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {uncovered.map((e) => (
              <span
                key={e.element_type}
                className="rounded-sm border border-[var(--color-border)] px-1.5 py-0.5 font-mono text-[11px] text-[var(--color-text-secondary)]"
              >
                {e.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default TakeoffRulesPage;
